/**
 * Admin metrics & logs board (plan section 17.3). Route: `/admin/metrics` (App.js/AdminRoute
 * wiring is the orchestrator's, per this phase's file-ownership list -- this page assumes it
 * is only ever reached by an authenticated ADMIN, the same guarantee `/admin/standards/import`
 * and `/criteria` already rely on).
 *
 * Filter-bar and BarChart recipes are copied from the main app's
 * `client/src/pages/AdminTokenUsagePage.js` (draft-then-apply state split, `queryParams`
 * useMemo, `niceYMax`/SVG bar geometry) -- see `components/BarChart.js` for the chart copy.
 * Tab recipe is `AnalysisDetailPage`'s tab-button pattern (border-b-2, active blue).
 *
 * Every tile, bar and table cell here reads from a `services/metricsApi` call -- there is no
 * client-side fabrication of a figure the backend does not supply. Two things worth noting
 * against plan 17.3's prose, now that `app/api/v1/admin_metrics.py` has actually landed:
 *
 *   1. The "what drove token usage" share bars are grouped by pipeline STAGE (DESCRIBE/MAP/
 *      VERIFY/TIEBREAK -- `overview.by_event_type`, a real column), not by ROLE as 17.3's
 *      prose says. A role grouping would need each event's actual (post-fallback) role, and
 *      `by_event_type` does not carry one -- DESCRIBE/MAP are nominally PRIMARY but silently
 *      become BACKUP on a Bedrock fallback. Labeling a nominal-role chart "by role" would
 *      misrepresent which model actually got billed, so this groups by the event type
 *      instead (`by_model[].role`, where the backend *does* know it per model, still drives
 *      the Role column in the By-model table).
 *   2. The Video filter is populated from `GET /admin/metrics/videos-list` -- a tenth route
 *      the backend agent added specifically because 17.2's original nine have no "list
 *      videos" endpoint (map section 6, item 5). It lists every video up front and narrows
 *      to one project's when the Project filter is set, rather than requiring a project
 *      first the way a per-project videos endpoint would have.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  RectangleStackIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ServerIcon,
  ArrowDownTrayIcon,
  ClipboardIcon,
} from '@heroicons/react/24/outline';
import { projectsAPI } from '../services/api';
import { metricsAPI } from '../services/metricsApi';
import { getApiErrorMessage } from '../utils/apiError';
import BarChart from '../components/BarChart';

// ---------------------------------------------------------------------------------------
// Static enums (this demo's own set, app/models/usage_metric.py::UsageEventType and
// app/models/pipeline_run.py::RunState) -- no API call needed for either.
// ---------------------------------------------------------------------------------------

const RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  // C6: the precomputed runs are dated 2026-09-05/06 and a restored database keeps those
  // timestamps -- a demo given long after the restore needs a range with no lower bound, or
  // every tile reads 0 with no error. Maps to `range=all` (admin_metrics.py:113-117).
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

const EVENT_TYPES = [
  'VIDEO_UPLOAD',
  'PIPELINE_RUN',
  'DESCRIBE',
  'MAP',
  'VERIFY',
  'TIEBREAK',
  'MERGE',
  'TRANSCRIBE',
  'CORRECT',
  'ASSESS',
  'REVIEW_ACTION',
  'REPORT',
];

// The model-call stages -- the subset of event types that ever carry token usage, and what the
// "what drove token usage" bars break down. CORRECT is the audio step's transcript corrector;
// TRANSCRIBE is billed by the minute and carries no tokens, so it has a label but no bar.
// ASSESS writes the rows of GRIHA's report at the end of a run (RUNLOG V8). MERGE is the merge
// judge that groups repeats of one problem before verifying (Task 1, 2026-09-27).
const MODEL_CALL_STAGES = ['DESCRIBE', 'MAP', 'MERGE', 'VERIFY', 'TIEBREAK', 'CORRECT', 'ASSESS'];

const STAGE_LABELS = {
  DESCRIBE: 'Describe',
  MAP: 'Map',
  VERIFY: 'Verify',
  TIEBREAK: 'Tiebreak',
  MERGE: 'Merge repeats',
  TRANSCRIBE: 'Transcribe',
  CORRECT: 'Correct transcript',
  ASSESS: 'Write GRIHA report',
};

const RUN_STATE_PILLS = {
  idle: { color: 'bg-gray-100 text-gray-800', label: 'Idle' },
  processing: { color: 'bg-blue-100 text-blue-800', label: 'Processing' },
  done: { color: 'bg-green-100 text-green-800', label: 'Done' },
  failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
};

const LOG_LEVELS = ['debug', 'info', 'warning', 'error'];

const TABS = [
  { id: 'usage', name: 'Usage', icon: ChartBarIcon },
  { id: 'pipeline', name: 'Pipeline', icon: RectangleStackIcon },
  { id: 'review', name: 'Review outcomes', icon: ClipboardDocumentCheckIcon },
  { id: 'logs', name: 'Logs', icon: DocumentTextIcon },
  { id: 'system', name: 'System', icon: ServerIcon },
];

// ---------------------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------------------

function formatNumber(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return new Intl.NumberFormat('en-US').format(Math.round(v));
}

function formatCost(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '$0.0000';
  return `$${v.toFixed(4)}`;
}

// Axis ticks only ("2M", "$0.2"); full values stay in the tooltip and every table.
function formatCompact(n) {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(n) || 0);
}

function formatCostCompact(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function formatMs(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `${Math.round(Number(n))} ms`;
}

/**
 * Turn a fetched CSV blob into a real, authenticated file download. `/events.csv`/`/runs.csv`
 * require the same Bearer token as every other admin route (see metricsApi.js's module
 * docstring) -- a plain `<a href>` would 401 the moment someone clicked it, so the button
 * calls this after an axios GET has already carried the header. `document.createElement`
 * plus a synthetic click is the standard way to hand the browser a same-origin-fetched blob
 * as a save-able file.
 */
function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function formatDateTime(value) {
  if (!value) return '—';
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasOffset ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id) {
  if (!id) return '—';
  return String(id).slice(0, 8);
}

// ---------------------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------------------

function RunStatePill({ state }) {
  const config = RUN_STATE_PILLS[state] || { color: 'bg-gray-100 text-gray-800', label: state || 'Unknown' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

function StatTile({ label, value, icon: Icon }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className="h-6 w-6 text-gray-400" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

// `source` is one of "usage_metrics" (finest-grained, has cache/latency/fallback data),
// "pipeline_runs" (a pre-P8 run's coarse per-run totals only), "mixed" (a filter window
// spanning both kinds of run) or "none" -- surfaced verbatim so a viewer can tell a
// coarse-but-real number from a fine-grained one, never silently blended.
const SOURCE_LABELS = {
  usage_metrics: { color: 'bg-green-100 text-green-800', label: 'Per-call' },
  pipeline_runs: { color: 'bg-amber-100 text-amber-800', label: 'Run total' },
  mixed: { color: 'bg-purple-100 text-purple-800', label: 'Mixed' },
  none: { color: 'bg-gray-100 text-gray-600', label: 'None' },
};

function SourceBadge({ source }) {
  const config = SOURCE_LABELS[source] || SOURCE_LABELS.none;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

function ShareBar({ label, share, tokens }) {
  const width = share > 0 ? Math.max(2, share) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">{formatNumber(tokens)} tokens ({share.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-2 bg-indigo-500 rounded-full" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Usage tab
// ---------------------------------------------------------------------------------------

// TimeSeriesPoint carries no display label, only a bare ISO date/hour string -- format it
// client-side the way every other date in this client is formatted.
function periodLabel(period) {
  if (!period) return '';
  const parsed = new Date(period.length <= 10 ? `${period}T00:00:00Z` : `${period}Z`);
  if (Number.isNaN(parsed.getTime())) return period;
  return period.length <= 10
    ? parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : parsed.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
}

function UsageTab({ appliedParams }) {
  const [graphMetric, setGraphMetric] = useState('tokens'); // tokens | cost | calls

  const { data: overview } = useQuery(
    ['metrics-overview', appliedParams],
    () => metricsAPI.getOverview(appliedParams),
    { select: (response) => response.data, keepPreviousData: true }
  );

  const { data: timeSeries, isLoading: isSeriesLoading } = useQuery(
    ['metrics-time-series', appliedParams],
    () => metricsAPI.getTimeSeries(appliedParams),
    { select: (response) => response.data, keepPreviousData: true }
  );

  // The server returns every numeric field per bucket (17.2's `metric` param is a no-op
  // filter, per admin_metrics.py's own docstring) -- so which one the chart plots is picked
  // here, client-side.
  const seriesField = graphMetric === 'cost' ? 'cost_usd' : graphMetric === 'calls' ? 'event_count' : 'tokens_total';
  const chartData = (timeSeries?.series || []).map((point) => ({
    period: point.period,
    periodLabel: periodLabel(point.period),
    value: point[seriesField],
  }));

  const formatValue = graphMetric === 'cost' ? formatCost : formatNumber;
  const formatAxis = graphMetric === 'cost' ? formatCostCompact : formatCompact;
  const metricLabel = graphMetric === 'cost' ? 'Cost' : graphMetric === 'calls' ? 'Calls' : 'Tokens';

  const byEventType = overview?.by_event_type || [];
  const stageRows = MODEL_CALL_STAGES.map((stage) => {
    const row = byEventType.find((e) => e.event_type === stage);
    return { stage, tokens: row?.tokens_total || 0 };
  });
  const stageTotal = stageRows.reduce((sum, r) => sum + r.tokens, 0);

  const byModel = overview?.by_model || [];

  const [isExporting, setIsExporting] = useState(false);
  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const response = await metricsAPI.getEventsCsv(appliedParams);
      downloadBlob(response.data, 'usage-events.csv');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not export events CSV'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-medium text-gray-900">Usage over time</h3>
          <div className="flex items-center gap-2">
            {['tokens', 'cost', 'calls'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setGraphMetric(m)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  graphMetric === m ? 'bg-blue-600 text-white' : 'bg-[#F8F8FF] text-gray-700 hover:bg-gray-100'
                }`}
              >
                {m === 'tokens' ? 'Tokens' : m === 'cost' ? 'Cost' : 'Calls'}
              </button>
            ))}
          </div>
        </div>
        {isSeriesLoading ? (
          <div className="h-40 animate-pulse bg-gray-100 rounded-lg" />
        ) : (
          <BarChart
            data={chartData}
            metricLabel={metricLabel}
            formatValue={formatValue}
            formatAxis={formatAxis}
            integer={graphMetric !== 'cost'}
          />
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">What drove token usage</h3>
        {stageTotal === 0 ? (
          <p className="text-sm text-gray-500">No model-call token usage recorded for this filter.</p>
        ) : (
          <div className="space-y-4">
            {stageRows.map((row) => (
              <ShareBar
                key={row.stage}
                label={STAGE_LABELS[row.stage]}
                tokens={row.tokens}
                share={(row.tokens / stageTotal) * 100}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">By model</h3>
          <button
            type="button"
            onClick={exportCsv}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        {byModel.length === 0 ? (
          <p className="text-sm text-gray-500">No model calls recorded for this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#F8F8FF]">
                <tr>
                  {['Model', 'Role', 'Calls', 'Input tokens', 'Output tokens', 'Cost', 'Avg latency', 'Fallbacks', 'Source'].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* One row per model AND role (a model can serve several: RUNLOG V8). */}
                {byModel.map((row) => (
                  <tr key={`${row.model_id}|${row.role || ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.model_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{row.role || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatNumber(row.calls)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatNumber(row.input_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatNumber(row.output_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatCost(row.cost_usd)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatMs(row.avg_latency_ms)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{row.fallback_count ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <SourceBadge source={row.source} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Pipeline tab
// ---------------------------------------------------------------------------------------

function PipelineTab({ projectId, videoId }) {
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const runsParams = { project_id: projectId, video_id: videoId, state: stateFilter, page, limit: 20 };

  const { data: runsData, isLoading } = useQuery(
    ['metrics-runs', runsParams],
    () => metricsAPI.getRuns(runsParams),
    { select: (response) => response.data, keepPreviousData: true }
  );

  const { data: runDetail } = useQuery(
    ['metrics-run-detail', selectedRunId],
    () => metricsAPI.getRun(selectedRunId),
    { select: (response) => response.data, enabled: !!selectedRunId }
  );

  const runs = runsData?.runs || [];

  const [isExporting, setIsExporting] = useState(false);
  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const response = await metricsAPI.getRunsCsv({ project_id: projectId, video_id: videoId, state: stateFilter });
      downloadBlob(response.data, 'pipeline-runs.csv');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not export runs CSV'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <label htmlFor="run-state-filter" className="text-sm font-medium text-gray-700">
              State
            </label>
            <select
              id="run-state-filter"
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-md border-gray-300 text-sm"
            >
              <option value="">All states</option>
              {Object.keys(RUN_STATE_PILLS).map((s) => (
                <option key={s} value={s}>
                  {RUN_STATE_PILLS[s].label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        {isLoading ? (
          <div className="h-32 animate-pulse bg-gray-100 rounded-lg" />
        ) : runs.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center">No runs match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#F8F8FF]">
                <tr>
                  {['Video', 'State', 'Stage', 'Started', 'Finished', 'Tokens', 'Cost', 'Matched/New/Missed', 'Budget', ''].map(
                    (h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{shortId(run.video_id)}</td>
                    <td className="px-4 py-3"><RunStatePill state={run.state} /></td>
                    <td className="px-4 py-3 text-sm text-gray-500">{run.stage || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDateTime(run.started_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDateTime(run.finished_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatNumber((run.usage?.input_tokens || 0) + (run.usage?.output_tokens || 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatCost(run.usage?.cost_usd)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {run.counts?.matched ?? '—'} / {run.counts?.new ?? '—'} / {run.counts?.missed ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{run.budget_exceeded ? 'Exceeded' : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedRunId(run.id)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={runs.length < 20}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {selectedRunId && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Run {shortId(selectedRunId)} — model calls</h3>
            <button type="button" onClick={() => setSelectedRunId(null)} className="text-sm text-gray-500 hover:text-gray-700">
              Close
            </button>
          </div>
          {(runDetail?.calls || []).length === 0 ? (
            <p className="text-sm text-gray-500">
              No per-call data recorded for this run — it predates per-call usage tracking, or none
              was ever emitted for it.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#F8F8FF]">
                  <tr>
                    {['Time', 'Stage', 'Role', 'Model', 'Tokens in/out', 'Cache read', 'Cost', 'Latency', 'Fallback', 'Error'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {runDetail.calls.map((call, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDateTime(call.timestamp)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{STAGE_LABELS[call.event_type] || call.event_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{call.role || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{call.model_id || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatNumber(call.tokens_input)} / {formatNumber(call.tokens_output)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {call.cache_read_tokens != null ? formatNumber(call.cache_read_tokens) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatCost(call.cost_usd)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatMs(call.latency_ms)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {call.fallback_used == null ? '—' : call.fallback_used ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate" title={call.error_details || ''}>
                        {call.error_details || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Review outcomes tab
// ---------------------------------------------------------------------------------------

function ReviewOutcomesTab({ appliedParams }) {
  const { data, isLoading } = useQuery(
    ['metrics-review-outcomes', appliedParams],
    () =>
      metricsAPI.getReviewOutcomes({
        project_id: appliedParams.project_id,
        start_date: appliedParams.start_date,
        end_date: appliedParams.end_date,
      }),
    { select: (response) => response.data }
  );

  const rows = data?.outcomes || [];

  if (isLoading) return <div className="h-32 animate-pulse bg-gray-100 rounded-lg" />;

  if (rows.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <p className="text-sm text-gray-500 text-center py-8">No review activity for this filter.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-[#F8F8FF]">
          <tr>
            {['Video', 'Approved', 'Rejected', 'Pending', 'Edited', 'Matched', 'New', 'Missed', 'Review minutes'].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.video_id}>
              <td className="px-4 py-3 text-sm text-gray-900">{row.video_title || shortId(row.video_id)}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.approved ?? 0}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.rejected ?? 0}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.pending ?? 0}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.edited ?? 0}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.matched ?? '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.new ?? '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-500">{row.missed ?? '—'}</td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {row.review_minutes != null ? `${row.review_minutes.toFixed(1)} min` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Logs tab -- follows a live run: polls /logs every 3s while Follow is on AND a run is
// actually processing (its own cheap 5s poll of GET /runs?state=processing&limit=1), and
// stops the moment nothing is running so an idle demo does not poll forever.
// ---------------------------------------------------------------------------------------

function LogsTab({ videoId }) {
  const [runId, setRunId] = useState('');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [follow, setFollow] = useState(true);

  const { data: activeRuns } = useQuery(
    ['metrics-active-runs', videoId],
    () => metricsAPI.getRuns({ video_id: videoId, state: 'processing', limit: 1 }),
    {
      select: (response) => response.data,
      refetchInterval: follow ? 5000 : false,
    }
  );
  const hasActiveRun = (activeRuns?.runs?.length || 0) > 0;

  const logsParams = { video_id: videoId, run_id: runId, level, search, limit: 200 };

  const { data: logsData, isLoading } = useQuery(
    ['metrics-logs', logsParams],
    () => metricsAPI.getLogs(logsParams),
    {
      select: (response) => response.data,
      refetchInterval: follow && hasActiveRun ? 3000 : false,
    }
  );

  const entries = logsData?.entries || [];

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        {/* Placeholders are not accessible names (a11y audit on this tab); the Pipeline
            tab's State select is the labelled pattern these follow. */}
        <input
          type="text"
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          placeholder="Run ID"
          aria-label="Run ID"
          className="rounded-md border-gray-300 text-sm w-40"
        />
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          aria-label="Log level"
          className="rounded-md border-gray-300 text-sm"
        >
          <option value="">All levels</option>
          {LOG_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search log text"
          aria-label="Search log text"
          className="rounded-md border-gray-300 text-sm flex-1 min-w-[160px]"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
          Follow
        </label>
        {follow && hasActiveRun && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Live — a run is processing
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="h-32 animate-pulse bg-gray-100 rounded-lg" />
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          No log entries match these filters. Entries only exist from the point the JSONL log
          processor shipped onward.
        </p>
      ) : (
        <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs text-gray-100 max-h-[480px] overflow-y-auto space-y-1">
          {entries.map((entry, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-gray-400 whitespace-nowrap">{entry.timestamp}</span>
              <span
                className={
                  entry.level === 'error'
                    ? 'text-red-400'
                    : entry.level === 'warning'
                      ? 'text-yellow-400'
                      : 'text-green-400'
                }
              >
                {entry.level}
              </span>
              <span className="text-gray-300">{entry.stage ? `[${entry.stage}] ` : ''}{entry.event}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// System tab
// ---------------------------------------------------------------------------------------

function ComponentCard({ label, component }) {
  const configured = !!component?.configured;
  return (
    <div className="bg-white shadow rounded-lg p-5">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-500">{label}</h4>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            configured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {configured ? 'Configured' : 'Not configured'}
        </span>
      </div>
      <p className="mt-2 text-xs text-gray-500 break-words">{component?.detail || '—'}</p>
    </div>
  );
}

function SystemTab() {
  const { data, isLoading } = useQuery('metrics-system', () => metricsAPI.getSystem(), {
    select: (response) => response.data,
  });

  // The server builds and returns the query itself (SystemStatusResponse.cloudwatch_query) --
  // this is a real, sourced string, not one assembled client-side from guessed field names.
  const cloudwatchQuery = data?.cloudwatch_query || '';

  const copyQuery = async () => {
    try {
      await navigator.clipboard.writeText(cloudwatchQuery);
      toast.success('Query copied');
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };

  if (isLoading) return <div className="h-32 animate-pulse bg-gray-100 rounded-lg" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <ComponentCard label="Database" component={data?.database} />
        <ComponentCard label="S3" component={data?.s3} />
        <ComponentCard label="Bedrock" component={data?.bedrock} />
        <div className="bg-white shadow rounded-lg p-5">
          <h4 className="text-sm font-medium text-gray-500">App</h4>
          <p className="mt-1 text-lg font-medium text-gray-900">v{data?.version || '—'}</p>
          <p className="mt-1 text-xs text-gray-500">{data?.environment || '—'}</p>
          <p className="mt-1 text-xs text-gray-500">
            {data?.force_mock_ai ? 'Mock AI on' : 'Mock AI off'} · {data?.s3_pending_uploads ?? 0} pending uploads
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">CloudWatch Logs Insights query</h3>
          <button
            type="button"
            onClick={copyQuery}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF]"
          >
            <ClipboardIcon className="h-4 w-4" />
            Copy
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">
          {cloudwatchQuery}
        </pre>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------------

export default function AdminMetricsPage() {
  const [activeTab, setActiveTab] = useState('usage');

  // Applied filters (drive every query below) vs draft filters (what the controls show
  // until Apply is pressed) -- the AdminTokenUsagePage recipe named in the brief.
  //
  // C6: '7d' used to be the default here. The precomputed runs are dated 2026-09-05/06 and a
  // database restored onto AWS keeps those timestamps, so a demo given more than a week after
  // the restore opened this board with every tile at 0 runs / 0 tokens / $0.0000 and no error
  // message -- it looked broken, and the PM had no way to know why
  // (AWS_DEPLOYMENT_PLAN.md section 6, C6). '30d' covers the gap between a rehearsal and a
  // demo a few weeks out; "All time" (above) covers the rest.
  const [range, setRange] = useState('30d');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [projectId, setProjectId] = useState('');
  const [videoId, setVideoId] = useState('');
  const [eventType, setEventType] = useState('');

  const [draftRange, setDraftRange] = useState(range);
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);
  const [draftProjectId, setDraftProjectId] = useState(projectId);
  const [draftVideoId, setDraftVideoId] = useState(videoId);
  const [draftEventType, setDraftEventType] = useState(eventType);

  // QA-65: 100 is the route's ceiling -- app/api/v1/projects.py declares
  // `limit: int = Query(50, ge=1, le=100)`, so the 200 this used to ask for was a guaranteed
  // 422 and the Project select silently offered nothing but "All projects". The error is
  // kept and rendered by the select below -- and tied to it with aria-describedby, so a
  // screen-reader user focusing a select that offers only "All projects" hears why -- so a
  // repeat of that cannot go unnoticed again.
  const { data: projects, isError: isProjectsError, error: projectsError } = useQuery(
    'metrics-projects-list',
    () => projectsAPI.list({ limit: 100 }),
    { select: (response) => response.data?.projects || [] }
  );

  // GET /admin/metrics/videos-list -- the endpoint built specifically to populate this
  // select (map section 6, item 5: 17.2's nine endpoints have no "list videos" route on
  // their own). Filtered by the draft project when one is chosen, but not gated behind it
  // -- unlike a per-project videos endpoint, this one lists every video up front.
  const { data: draftVideos } = useQuery(
    ['metrics-videos-list', draftProjectId],
    () => metricsAPI.getVideosList({ project_id: draftProjectId || undefined }),
    { select: (response) => response.data?.videos || [] }
  );

  const applyFilters = () => {
    setRange(draftRange);
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setProjectId(draftProjectId);
    setVideoId(draftVideoId);
    setEventType(draftEventType);
  };

  const appliedParams = useMemo(() => {
    const base = {
      project_id: projectId || undefined,
      video_id: videoId || undefined,
      event_type: eventType || undefined,
    };
    return range === 'custom' ? { ...base, start_date: startDate, end_date: endDate } : { ...base, range };
  }, [projectId, videoId, eventType, range, startDate, endDate]);

  const { data: overview, isLoading: isOverviewLoading, isError: isOverviewError, error: overviewError } = useQuery(
    ['metrics-overview', appliedParams],
    () => metricsAPI.getOverview(appliedParams),
    { select: (response) => response.data, keepPreviousData: true }
  );

  return (
    <div className="p-6">
      <div className="max-w-8xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Metrics &amp; logs</h1>
          <p className="mt-1 text-sm text-gray-500">Model usage, cost, pipeline runs and review outcomes.</p>
        </div>

        {isOverviewError ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-sm text-red-600">{getApiErrorMessage(overviewError, 'Could not load metrics')}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <StatTile label="Runs" value={isOverviewLoading ? '—' : formatNumber(overview?.total_runs)} icon={RectangleStackIcon} />
          <StatTile
            label="Total tokens"
            value={isOverviewLoading ? '—' : formatNumber(overview?.total_tokens)}
            icon={ChartBarIcon}
          />
          <StatTile
            label="Total cost"
            value={isOverviewLoading ? '—' : formatCost(overview?.total_cost_usd)}
            icon={ClipboardDocumentCheckIcon}
          />
          <StatTile
            label="Cache read tokens"
            value={isOverviewLoading ? '—' : formatNumber(overview?.total_cache_read_tokens)}
            icon={ServerIcon}
          />
        </div>

        {/* Filter bar: draft-then-apply (AdminTokenUsagePage recipe). */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label htmlFor="filter-range" className="block text-xs font-medium text-gray-500 mb-1">
                Range
              </label>
              <select
                id="filter-range"
                value={draftRange}
                onChange={(e) => setDraftRange(e.target.value)}
                className="rounded-md border-gray-300 text-sm"
              >
                {RANGE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {draftRange === 'custom' && (
              <>
                <div>
                  <label htmlFor="filter-start" className="block text-xs font-medium text-gray-500 mb-1">
                    Start date
                  </label>
                  <input
                    id="filter-start"
                    type="date"
                    value={draftStartDate}
                    onChange={(e) => setDraftStartDate(e.target.value)}
                    className="rounded-md border-gray-300 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="filter-end" className="block text-xs font-medium text-gray-500 mb-1">
                    End date
                  </label>
                  <input
                    id="filter-end"
                    type="date"
                    value={draftEndDate}
                    onChange={(e) => setDraftEndDate(e.target.value)}
                    className="rounded-md border-gray-300 text-sm"
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="filter-project" className="block text-xs font-medium text-gray-500 mb-1">
                Project
              </label>
              <select
                id="filter-project"
                value={draftProjectId}
                onChange={(e) => {
                  setDraftProjectId(e.target.value);
                  setDraftVideoId('');
                }}
                aria-describedby={isProjectsError ? 'filter-project-error' : undefined}
                className="rounded-md border-gray-300 text-sm"
              >
                <option value="">All projects</option>
                {(projects || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {isProjectsError && (
                <p id="filter-project-error" className="mt-1 text-xs text-gray-500">
                  Project list unavailable: {getApiErrorMessage(projectsError, 'request failed')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="filter-video" className="block text-xs font-medium text-gray-500 mb-1">
                Video
              </label>
              <select
                id="filter-video"
                value={draftVideoId}
                onChange={(e) => setDraftVideoId(e.target.value)}
                className="rounded-md border-gray-300 text-sm"
              >
                <option value="">All videos</option>
                {(draftVideos || []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="filter-event-type" className="block text-xs font-medium text-gray-500 mb-1">
                Event type
              </label>
              <select
                id="filter-event-type"
                value={draftEventType}
                onChange={(e) => setDraftEventType(e.target.value)}
                className="rounded-md border-gray-300 text-sm"
              >
                <option value="">All types</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Tabs (AnalysisDetailPage recipe). */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {activeTab === 'usage' && <UsageTab appliedParams={appliedParams} />}
        {activeTab === 'pipeline' && <PipelineTab projectId={projectId} videoId={videoId} />}
        {activeTab === 'review' && <ReviewOutcomesTab appliedParams={appliedParams} />}
        {activeTab === 'logs' && <LogsTab videoId={videoId} />}
        {activeTab === 'system' && <SystemTab />}
      </div>
    </div>
  );
}
